import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { notification } from "antd";
import { PdfLambda } from "../../../Actions/PrintActions";
import { DigitalShareAppType } from "../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../Enums/DigitalShareItemType";
import { IDigitalShareItem } from "../../../Models/IDigitalShareItem";
import {
    digitalShareRender,
    getPrintOutputs,
} from "../../../Util/ScoutDigitalShareUtil";
import {
    GenerationFailureReason,
    TemplateImageGenerator,
    TemplateImageGeneratorProgressStep,
} from "../../../Util/TemplateImageGenerator";
import { zipAndDownloadImages } from "../../../Util/ZipFileCreator";
import DigitalShareDialog from "./Dialogue/DigitalShareDialogue";
import {
    ExtraProgressStep,
    getProgressDisplayDetails,
    IDigitalShareProgress,
} from "./Dialogue/ProgressLogic";
import { RenderCollection, RenderContainer } from "./DigitalShareCommonStyles";
import { ProgressScreen } from "./ProgressScreen";
import { RenderType } from "../../../Enums/Enums";

export const CHILD_HEIGHT = 503;
export const MAX_WAIT_FOR_SHARE_COMPONENTS_SECONDS = 60;
export const MAX_WAIT_FOR_PRINT_COMPONENTS_SECONDS = 360;

interface IDigitalShareGenerator {
    onChangeGenerating: (isGenerating: boolean) => void;
    onClose: () => void;
    app: DigitalShareAppType;
    isChoosingDigitalShare: boolean;
    outputs: IDigitalShareItem[];
}

export const digitalShareRefs: Map<
    string,
    Map<string, React.MutableRefObject<HTMLDivElement>>
> = new Map<string, Map<string, React.MutableRefObject<HTMLDivElement>>>();

export const selectedOutputsMap: Map<string, string[]> = new Map<
    string,
    string[]
>();

export const isOutputSelected = (app: string, itemType: string): boolean => {
    const outputs: string[] = selectedOutputsMap.get(app);

    if (outputs?.indexOf(itemType) > -1) {
        return true;
    }

    return false;
};
export const mapSnapshots: Map<string, JSX.Element> = new Map();

export const registerRef = (
    app: string,
    itemType: string,
    ref: React.MutableRefObject<HTMLDivElement>
) => {
    let refMap = digitalShareRefs.get(app);
    if (!refMap) {
        digitalShareRefs.set(
            app,
            new Map<string, React.MutableRefObject<HTMLDivElement>>()
        );
        refMap = digitalShareRefs.get(app);
    }
    refMap.set(itemType, ref);
};

const digitalShareElements: Map<string, Map<string, React.ReactElement>>
    = new Map<string, Map<string, React.ReactElement>>();
export const digitalShareElementText: Map<string, string | React.ReactElement>
    = new Map<string, string | React.ReactElement>();

export const registerElement = (
    app: string,
    itemType: string,
    itemText: string | React.ReactElement,
    element: React.ReactElement
): void => {
    let elementMap = digitalShareElements.get(app);

    if (!elementMap) {
        digitalShareElements.set(app, new Map<string, React.ReactElement>());

        elementMap = digitalShareElements.get(app);
    }

    digitalShareElementText.set(itemType, itemText);
    elementMap.set(itemType, element);
};

export const pendingRenders: Map<string, Map<string, boolean>> = new Map<
    string,
    Map<string, boolean>
>();

export const setPendingRender = (
    app: string,
    item: string,
    isPending: boolean
) => {
    console.log("setPendingRender() to", isPending);
    if (!pendingRenders.has(app)) {
        pendingRenders.set(app, new Map<string, boolean>());
    }
    pendingRenders.get(app).set(item, isPending);
};

export const clearPendingRenders = (app: string) => {
    if (pendingRenders.has(app)) {
        pendingRenders.get(app).clear();
    }
};

export const generationStatus: Map<string, boolean> = new Map<
    string,
    boolean
>();

export const setGenerationStatus = (app: string, isGenerating: boolean) => {
    generationStatus.set(app, isGenerating);
};

const getDefaultProgress = (): IDigitalShareProgress => ({
    succeeded: 0,
    failed: 0,
    total: 0,
});

export const DigitalShareGenerator: FunctionComponent<IDigitalShareGenerator>
    = (props: IDigitalShareGenerator) => {
        const [selectedOutputs, setSelectedOutputs] = useState<
            DigitalShareItemType[]
        >([]);
        const [templateImageGenerator, setTemplateImageGenerator]
            = useState<TemplateImageGenerator>(null);
        const [isGeneratingDigitalShare, setIsGeneratingDigitalShare]
            = useState(false);
        const [didDownloadImages, setDidDownloadImages] = useState(false);
        const [generationProgress, setGenerationProgress]
            = useState<IDigitalShareProgress>(getDefaultProgress());
        const [percentComplete, setPercentComplete] = useState<number>(0);
        const [progressText, setProgressText] = useState<string>("Initializing...");
        const [progressStep, setProgressStep] = useState<
            TemplateImageGeneratorProgressStep | ExtraProgressStep
        >(ExtraProgressStep.Initializing);

        const onImageGenerationProgress = (
            succeeded: number,
            failed: number,
            total: number,
            step: TemplateImageGeneratorProgressStep
        ) => {
            setGenerationProgress({ succeeded, failed, total });
            setProgressStep(step);
        };

        const renderDigitalShare = (outputs: DigitalShareItemType[]) => {
            selectedOutputsMap.set(props.app, outputs);

            setSelectedOutputs(outputs);
            setIsGeneratingDigitalShare(true);
        };

        const updateProgress = () => {
            const { text, percentComplete } = getProgressDisplayDetails(
                selectedOutputs,
                progressStep,
                generationProgress,
                RenderType.DIGITAL_SHARE
            );
            setProgressText(text);
            setPercentComplete(percentComplete);
        };

        useEffect(() => {
            updateProgress();
        }, [generationProgress, progressStep]);

        useEffect(() => {
            let isComponentMounted = true;
            let cleanupTimeout = null;
            if (isGeneratingDigitalShare && !templateImageGenerator) {
                if (digitalShareRefs.has(props.app)) {
                    digitalShareRefs.get(props.app).clear();
                } else {
                    digitalShareRefs.set(
                        props.app,
                        new Map<string, React.MutableRefObject<HTMLDivElement>>()
                    );
                }
                const generator = new TemplateImageGenerator(
                    selectedOutputs,
                    digitalShareRefs.get(props.app),
                    onImageGenerationProgress
                );
                setTemplateImageGenerator(generator);
                updateProgress();
                const generate = async () => {
                    const start = window.performance.now();

                    // Basically, give child components the ability to check whether
                    // exporting has been canceled.
                    setGenerationStatus(props.app, true);

                    // We were originally doing the rest of this function in a set timeout.
                    // It seems that we don't need to wait at all if animations are disabled.
                    // Any initialization that happens in hooks, etc, apparently happens
                    // before the stack is cleared.
                    props.onChangeGenerating(true);

                    // Wait here so the progress screen images have a chance to load.
                    // Without this, the generation process will block the UI thread, meaning
                    // the progress screen will have missing images at first.
                    // Note: Commenting out as it doesn't seem to be making a difference, and could
                    // possibly interfere with error handling.
                    // await delay(1000);
                    let wasError = false;
                    let error: any = null;
                    try {
                        const images = await generator.generate(
                            3000,
                            1000 * MAX_WAIT_FOR_SHARE_COMPONENTS_SECONDS
                        );
                        const elapsed = (window.performance.now() - start) / 1000.0;
                        console.log(`Generating ${images.length} took ${elapsed} seconds`);

                        if (!isComponentMounted) {
                            return;
                        }
                        setProgressStep(ExtraProgressStep.Zipping);
                        if (!isComponentMounted) {
                            return;
                        }
                        await zipAndDownloadImages(
                            `${props.app}Exports.zip`,
                            images.map((r) => ({
                                name: `${r.type}.png`,
                                dataUrl: r.dataUrl,
                            }))
                        );
                    } catch (err) {
                        wasError = true;
                        error = err;
                    } finally {
                        // By setting this, children will know they need to stop operations
                        // if they are still running.
                        // (They shouldn't be running anyway if the process finished
                        // successfully.)
                        setGenerationStatus(props.app, false);
                        const cleanup = () => {
                            if (!isComponentMounted) {
                                return;
                            }
                            const pending = pendingRenders.get(props.app);
                            let hasPending = false;
                            if (pending) {
                                pending.forEach((val: boolean, key: string) => {
                                    if (val) {
                                        hasPending = true;
                                    }
                                });
                                if (hasPending) {
                                    cleanupTimeout = setTimeout(cleanup, 100);
                                    return;
                                }
                            }

                            // Reset pending renders
                            clearPendingRenders(props.app);

                            if (!isComponentMounted) {
                                return;
                            }
                            // Let the client know we're done generating
                            props.onChangeGenerating(false);

                            if (!isComponentMounted) {
                                return;
                            }
                            setProgressStep(ExtraProgressStep.DownloadComplete);
                            if (!isComponentMounted) {
                                return;
                            }
                            setDidDownloadImages(true);
                            if (!isComponentMounted) {
                                return;
                            }
                            setTemplateImageGenerator(null);
                            if (!isComponentMounted) {
                                return;
                            }
                            digitalShareElements.clear();
                            props.onClose();

                            if (wasError) {
                                let description = "There was a problem exporting your images.";
                                if (error === GenerationFailureReason.TimeoutExceeded) {
                                    description
                                        = "It took too long to export your images. Please try closing some running programs on your device, or select fewer outputs at a time.";
                                } else if (typeof error === "string") {
                                    description = error;
                                }
                                notification.error({
                                    message: "Error exporting images",
                                    description,
                                    duration: null,
                                });
                            }
                        };
                        cleanup();
                    }
                };

                generate();
            }
            return () => {
                isComponentMounted = false;
                clearTimeout(cleanupTimeout);
            };
        }, [isGeneratingDigitalShare, selectedOutputs]);

        const [isDialogueVisible, setIsDialogueVisible] = useState(true);
        const [elementArr, setElementArr] = useState([]);

        useEffect(() => {
            if (!isDialogueVisible) {
                props.onClose();
            }
        }, [isDialogueVisible]);

        useEffect(() => {
            const elements = digitalShareElements.get(props.app);
            const temp = [];
            if (elements) {
                for (const [k, v] of elements) {
                    temp.push({
                        key: k,
                        element: v,
                    });
                }
                setElementArr(temp);
            }
        }, [digitalShareElements.get(props.app)?.size]);

        return (
            <>
                {isGeneratingDigitalShare && (
                    <ProgressScreen
                        app={props.app}
                        text={progressText}
                        percentComplete={percentComplete}
                        onClose={null}
                        downloaded={didDownloadImages}
                    />
                )}
                {props.isChoosingDigitalShare && (
                    <DigitalShareDialog
                        app={props.app}
                        onClose={() => setIsDialogueVisible(false)}
                        onSubmit={renderDigitalShare}
                        outputs={props.outputs}
                    />
                )}
                <RenderCollection>
                    {isGeneratingDigitalShare
                        && elementArr.map((v, i) => (
                            <RenderContainer
                                key={i}
                                data-testclass={"digital-share-template-rcg"}
                                ref={digitalShareRefs.get(props.app).get(v.key)}
                            >
                                {digitalShareRender(props.app, v.key, v.element)}
                            </RenderContainer>
                        ))}
                </RenderCollection>
            </>
        );
    };

interface IPrintGenerator {
    onChangeGenerating: (isGenerating: boolean) => void;
    onComplete: () => void;
    app: DigitalShareAppType;
    outputs: IDigitalShareItem[];
    jobId: string;
}

export const PrintGenerator: FunctionComponent<IPrintGenerator> = (
    props: IPrintGenerator
) => {
    const outputs = getPrintOutputs(props.app, props.outputs).map((o) => o.type);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationProgress, setGenerationProgress]
        = useState<IDigitalShareProgress>(getDefaultProgress());
    const [percentComplete, setPercentComplete] = useState<number>(0);
    const [progressText, setProgressText] = useState<string>("Initializing...");
    const [progressStep, setProgressStep] = useState<
        TemplateImageGeneratorProgressStep | ExtraProgressStep
    >(ExtraProgressStep.Initializing);
    const [elementArr, setElementArr] = useState([]);
    const formRef = useRef<HTMLFormElement>(null);

    const onImageGenerationProgress = (
        succeeded: number,
        failed: number,
        total: number,
        step: TemplateImageGeneratorProgressStep
    ) => {
        setGenerationProgress({ succeeded, failed, total });
        setProgressStep(step);
    };

    const updateProgress = () => {
        const { text, percentComplete } = getProgressDisplayDetails(
            outputs,
            progressStep,
            generationProgress,
            RenderType.PRINT
        );
        setProgressText(text);
        setPercentComplete(percentComplete);
    };

    useEffect(() => {
        updateProgress();
    }, [generationProgress, progressStep]);

    useEffect(() => {
        let isComponentMounted = true;
        let cleanupTimeout = null;
        if (!isGenerating) {
            setIsGenerating(true);
            selectedOutputsMap.set(props.app, outputs);
            if (digitalShareRefs.has(props.app)) {
                digitalShareRefs.get(props.app).clear();
            } else {
                digitalShareRefs.set(
                    props.app,
                    new Map<string, React.MutableRefObject<HTMLDivElement>>()
                );
            }
            const generator = new TemplateImageGenerator(
                outputs,
                digitalShareRefs.get(props.app),
                onImageGenerationProgress
            );
            updateProgress();
            const generate = async () => {
                const start = window.performance.now();

                // Basically, give child components the ability to check whether
                // exporting has been canceled.
                setGenerationStatus(props.app, true);

                // We were originally doing the rest of this function in a set timeout.
                // It seems that we don't need to wait at all if animations are disabled.
                // Any initialization that happens in hooks, etc, apparently happens
                // before the stack is cleared.
                props.onChangeGenerating(true);

                // Wait here so the progress screen images have a chance to load.
                // Without this, the generation process will block the UI thread, meaning
                // the progress screen will have missing images at first.
                // Note: Commenting out as it doesn't seem to be making a difference, and could
                // possibly interfere with error handling.
                // await delay(1000);
                let wasError = false;
                let error: any = null;
                try {
                    const images = await generator.generate(
                        3000,
                        1000 * MAX_WAIT_FOR_PRINT_COMPONENTS_SECONDS
                    );
                    const elapsed = (window.performance.now() - start) / 1000.0;
                    console.log(`Generating ${images.length} took ${elapsed} seconds`);

                    if (!isComponentMounted) {
                        return;
                    }
                    setProgressStep(ExtraProgressStep.BuildingPDF);
                    if (!isComponentMounted) {
                        return;
                    }

                    let dataUrls = images.map((i) => {
                        const newImage = new Image();
                        newImage.src = i.dataUrl;
                        // Resize the image to fit in the browser
                        newImage.style.width = "100%";
                        newImage.style.height = "auto";

                        return {
                            html: newImage.outerHTML,
                            width: 1500,// This size exactly because of the template
                            height: 835,
                        };
                    });

                    // Use window.testPrintTimeoutFactor to induce a longer-running AJAX call
                    // on the server side. Useful for testing error messages client-side and
                    // load balancer request limits.
                    // if (
                    //     (window as any).testPrintTimeoutFactor !== undefined
                    //     && typeof (window as any).testPrintTimeoutFactor === "number"
                    // ) {
                    //     const factor = (window as any).testPrintTimeoutFactor;
                    //     let extraDataUrls = dataUrls;
                    //     for (let i = 0; i < factor; i++) {
                    //         extraDataUrls = [...extraDataUrls, ...dataUrls];
                    //     }
                    //     dataUrls = extraDataUrls;
                    // }

                    // const product = props.app.replace(" ", "");

                    const b64Pdf = await PdfLambda(dataUrls);

                    if (!isComponentMounted) {
                        return;
                    }

                    // window.open(url, "_blank");

                    // Convert Base64 to Byte Array
                    const binaryString: string = atob(b64Pdf);
                    const len = binaryString.length;
                    const bytes = new Uint8Array(len);
                    for (let i = 0; i < len; ++i) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    // Open pdf in new tab
                    const file = new Blob([bytes], { type: "application/pdf" });
                    const fileURL = URL.createObjectURL(file);
                    window.open(fileURL);

                } catch (err) {
                    wasError = true;
                    error = err;
                } finally {
                    // By setting this, children will know they need to stop operations
                    // if they are still running.
                    // (They shouldn't be running anyway if the process finished
                    // successfully.)
                    setGenerationStatus(props.app, false);
                    const cleanup = () => {
                        if (!isComponentMounted) {
                            return;
                        }
                        const pending = pendingRenders.get(props.app);
                        let hasPending = false;
                        if (pending) {
                            pending.forEach((val: boolean, key: string) => {
                                if (val) {
                                    hasPending = true;
                                }
                            });
                            if (hasPending) {
                                cleanupTimeout = setTimeout(cleanup, 100);
                                return;
                            }
                        }

                        // Reset pending renders
                        clearPendingRenders(props.app);

                        digitalShareElements.clear();

                        if (!isComponentMounted) {
                            return;
                        }
                        setProgressStep(ExtraProgressStep.DownloadComplete);
                        if (!isComponentMounted) {
                            return;
                        }
                        // Let the client know we're done generating
                        props.onChangeGenerating(false);

                        if (!isComponentMounted) {
                            return;
                        }
                        props.onComplete();

                        if (wasError) {
                            let description = "There was a problem exporting your images.";
                            if (error === GenerationFailureReason.TimeoutExceeded) {
                                description
                                    = "It took too long to export your images. Please try closing some running programs on your device.";
                            } else if (typeof error === "string") {
                                description = error;
                            }
                            notification.error({
                                message: "Error exporting images",
                                description,
                                duration: null,
                            });
                        }
                    };
                    cleanup();
                }
            };

            generate();
        }
        return () => {
            isComponentMounted = false;
            clearTimeout(cleanupTimeout);
        };
    }, []);

    useEffect(() => {
        const elements = digitalShareElements.get(props.app);
        const temp = [];
        if (elements) {
            for (const [k, v] of elements) {
                temp.push({
                    key: k,
                    element: v,
                });
            }
            setElementArr(temp);
        }
    }, [digitalShareElements.get(props.app)?.size]);

    return (
        <>
            <ProgressScreen
                text={progressText}
                app={props.app}
                percentComplete={percentComplete}
                onClose={null}
                downloaded={false}
            />
            <form target="_blank" ref={formRef}></form>
            {elementArr.map((v, i) => (
                <div
                    data-testclass={"digital-share-template-rcg"}
                    ref={digitalShareRefs.get(props.app).get(v.key)}
                    style={{
                        width: "1500px",
                        height: "834px",
                        position: "absolute",
                        top: "0",
                        left: "0",
                    }}
                    key={i}
                >
                    {digitalShareRender(props.app, v.key, v.element)}
                </div>
            ))}
        </>
    );
};
